import animation, { fadeInUp } from '@helpers/animation';
import brand from '@helpers/brand';
import fonts from '@helpers/fonts';
import { ButtonReset } from '@helpers/global';
import { Device, from, until } from '@helpers/media';
import { ButtonStyles } from '@stories/Elements/Forms/Buttons/Button.styles';
import styled, { css } from 'styled-components';

const Card = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  border-radius: 4px;
  overflow: hidden;

  ${ButtonStyles.ButtonElement}, ${ButtonStyles.LinkElement} {
    margin-top: auto;

    @media ${until(Device.MobileLarge)} {
      font-size: 11px;
    }
  }
`;

const CardContent = styled.a<{
  showBorder: boolean;
  borderPadding: boolean;
  inView: boolean;
  delay: number;
  background?: 'white' | 'none';
  isLink: boolean;
}>`
  display: flex;
  flex-direction: column;
  flex-wrap: nowrap;
  flex-grow: 1;
  align-items: center;
  text-align: center;
  color: unset;
  z-index: 1;

  ${({ isLink }) =>
    isLink
      ? null
      : css`
          cursor: default;
        `}

  @media ${until(Device.Mobile)} {
    ${() => MediaWrapper} {
      max-width: 150px;
    }
  }

  ${({ background }) => {
    switch (background) {
      case 'white':
        return css`
          background-color: ${brand.white};
          color: ${brand.grey[35]};
        `;

      case 'none':
      default:
        return css`
          background-color: transparent;
          color: ${brand.white};
          border: 1px solid ${brand.white} !important;
        `;
    }
  }}

  ${({ showBorder }) =>
    showBorder
      ? css`
          border-top: 5px solid ${brand.red.base};
        `
      : css`
          border-top: 5px solid ${brand.white};
        `};

  ${({ borderPadding }) =>
    borderPadding
      ? css`
          padding: 28px 6px 16px;
          [data-has-new='true'] & {
            padding-top: 35px;
          }

          @media ${from(Device.MobileLarge)} {
            padding: 28px 12px 16px;
            [data-has-new='true'] & {
              padding-top: 35px;
            }
          }

          @media ${from(Device.TabletLarge)} {
            padding: 35px 24px 20px;
          }
        `
      : css`
          padding: 16px 6px;

          @media ${from(Device.MobileLarge)} {
            padding: 16px 12px;
          }

          @media ${from(Device.TabletLarge)} {
            padding: 20px 24px;
          }
        `}

  ${({ inView, delay }) => (inView !== undefined ? fadeInUp(inView, 25, 500, delay, animation.easeInOutCubic) : null)};
`;

const MediaWrapper = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-wrap: wrap;
  aspect-ratio: 1;
`;

const Media = styled.div`
  position: relative;
  margin-bottom: 8px;
  align-self: normal;
  min-width: 141px;
  min-height: 0px;

  @media ${from(Device.Tablet)} {
    margin-bottom: 16px;
  }

  img {
    height: 100%;
    object-fit: contain;
    width: 100%;
  }
`;

const Icons = styled.div`
  --margin: 8px;
  --height: 50px;
  display: flex;
  flex-direction: row;
  margin-bottom: var(--margin);
  margin-top: var(--margin);
  gap: 6px;
  height: var(--height);

  @media ${from(Device.Tablet)} {
    --margin: 16px;
    --height: 67px;
    gap: 20px;
  }
`;

const IconWrapper = styled.div`
  border: 1px solid ${brand.red.base};
  overflow: hidden;
  border-radius: 4px;
  display: flex;
  flex-direction: column;

  span {
    margin-top: auto;
    background-color: ${brand.red.base};
    color: ${brand.white};
    font-size: 10px;
    padding: 3px 9px;
    ${fonts.helvetica['63-medium-extended']};

    @media ${from(Device.Tablet)} {
      font-size: 13px;
    }
  }
`;

const IconInner = styled.div`
  padding: 4px 9px;
  display: flex;
  justify-content: center;
  align-items: center;

  @media ${from(Device.Tablet)} {
    padding: 8px 11px;
  }

  svg {
    height: 24px;

    @media ${from(Device.Tablet)} {
      height: 30px;
    }
  }
`;

const Name = styled.span`
  ${fonts.helvetica['93-black-extended']};
  text-transform: uppercase;
  margin: 12px auto;
  font-size: 12px;
  line-height: 1.2;
  display: block;
  letter-spacing: ${fonts.ls(20)};
  hyphens: auto;
  position: relative;
  overflow-wrap: break-word;

  [data-has-stars='true'] & {
    margin-top: 0;
  }

  &[data-longest-title] {
    display: grid;
    grid-template-areas: 'text';

    & > span,
    &:before {
      left: 0;
      top: 0;
      width: 100%;
      overflow: hidden;
      grid-area: text;
    }

    &:before {
      content: attr(data-longest-title);
      visibility: hidden;
    }
  }

  @media ${from(Device.Tablet)} {
    margin: 16px auto;
    padding: 0 10px;

    [data-has-stars='true'] & {
      margin-top: 0;
    }
  }

  @media ${from(Device.DesktopLarge)} {
    font-size: 16px;
  }
`;

const Unpublished = styled.div`
  margin-bottom: 18px;
  color: ${brand.red.base};
  font-size: 24px;
  height: 24px;

  [data-has-new='true'] & {
    margin-top: 18px;
  }

  @media ${until(Device.Tablet)} {
    font-size: 20px;
    height: 20px;
  }
`;

const CodeWrapper = styled.div`
  position: relative;
  margin-bottom: 18px;

  @media ${from(Device.Tablet)} {
    display: flex;
    align-items: flex-end;
    justify-content: center;
    width: 100%;
  }
`;

const Code = styled.span`
  ${fonts.helvetica['73-bold-extended']};
  display: block;
  font-size: 12px;
  color: ${brand.grey[35]};
  line-height: 1;
  letter-spacing: ${fonts.ls(20)};

  @media ${from(Device.Tablet)} {
    font-size: 14px;
    order: 1;
  }

  @media ${from(Device.DesktopLarge)} {
    font-size: 16px;
  }
`;

const TagWrapper = styled.div`
  position: absolute;
  top: -1px;
  left: 50%;
  transform: translateX(-50%);
  width: 100%;
`;

const ProductStandardPrice = styled.p`
  ${fonts.helvetica['93-black-extended']};
  color: ${brand.black};
  font-size: 14px;
  line-height: 1em;
  text-decoration: line-through;
  margin-bottom: 4px;

  display: flex;
  align-items: center;

  span {
    ${fonts.helvetica['63-medium-extended']};
    font-size: 14px;
    margin-left: 6px;
  }
`;

const ProductPrice = styled.p`
  ${fonts.helvetica['93-black-extended']};
  color: ${brand.red.base};
  font-size: 24px;
  line-height: 1em;
  margin-bottom: 16px;

  display: flex;
  align-items: center;

  span {
    ${fonts.helvetica['63-medium-extended']};
    font-size: 14px;
    margin-left: 6px;
  }

  @media ${from(Device.Tablet)} {
    margin-bottom: 16px;
  }

  @media ${from(Device.DesktopLarge)} {
    font-size: 26px;
  }
`;

const Disclaimer = styled.span`
  ${fonts.helvetica['63-medium-extended']}
  color: ${brand.nearlyBlack};
  font-size: 12px;
  padding: 0 12px;
  margin-bottom: 16px;
  text-transform: uppercase;
  text-align: center;

  @media ${from(Device.Tablet)} {
    margin-bottom: 20px;
  }
`;

const BundleDropdown = styled.div<{ isActive: boolean; inView: boolean; delay: number }>`
  position: relative;
  color: ${brand.white};
  width: 100%;
  z-index: 5;

  ${({ inView, delay }) => (inView !== undefined ? fadeInUp(inView, 25, 500, delay, animation.easeInOutCubic) : null)};

  ${({ isActive }) =>
    isActive &&
    css`
      ${() => BottomTitle} {
        border-bottom-left-radius: unset;
        border-bottom-right-radius: unset;
      }

      ${() => BottomContent} {
        opacity: 1;
        pointer-events: all;
      }

      ${() => Icon} svg:nth-child(2) {
        transform: rotate(180deg);
      }
    `}
`;

const BottomTitle = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  ${fonts.helvetica['83-heavy-extended']};
  font-size: 14px;
  line-height: 1.2;
  padding: 0 20px;
  height: 50px;
  text-transform: uppercase;
  background-color: ${brand.grey[20]};
  border-bottom-left-radius: 4px;
  border-bottom-right-radius: 4px;
  cursor: pointer;
  transition: border-radius 300ms ease-out;
`;

const BottomContent = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  width: 100%;
  padding: 0 20px 20px;
  background-color: ${brand.grey[20]};
  border-bottom-left-radius: 4px;
  border-bottom-right-radius: 4px;
  opacity: 0;
  pointer-events: none;
  transition: opacity 200ms ease-out;

  ul {
    li {
      position: relative;
      ${fonts.helvetica['53-extended']}
      font-size: 14px;
      line-height: 1.4;
      padding-left: 20px;
      margin-bottom: 8px;

      &:last-child {
        margin-bottom: unset;
      }

      &:before {
        content: '';
        display: block;
        position: absolute;
        top: 50%;
        left: 0;
        transform: translateY(-50%);
        width: 6px;
        height: 6px;
        border-radius: 50%;
        background-color: ${brand.white};
      }
    }
  }
`;

const Icon = styled.div`
  position: relative;
  width: 14px;
  height: 14px;
  color: ${brand.white};

  svg {
    position: absolute;
    top: 50%;
    margin-top: -2px;
    left: 0;
    width: 100%;

    &:nth-child(2) {
      transform: rotate(90deg);
      transition: transform 200ms ease-out;
    }
  }
`;

const LogoWrapper = styled.div`
  height: 35px;
  align-self: stretch;
  margin-bottom: 12px;

  img {
    height: 100%;
  }
`;

const CompareContainer = styled.div<{
  inView: boolean;
  delay: number;
}>`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 10px 20px;
  background-color: ${brand.grey[80]};
  cursor: pointer;
  user-select: none;

  &[data-added='true'] {
    background-color: ${brand.red.base};

    ${() => CompareText} {
      color: ${brand.white};
    }

    ${() => CompareBox} {
      svg {
        display: block;
      }
    }
  }

  ${({ inView, delay }) => (inView !== undefined ? fadeInUp(inView, 25, 500, delay, animation.easeInOutCubic) : null)};
`;

const CompareBox = styled.div`
  width: 20px;
  height: 20px;
  border: 1px solid ${brand.black};
  background-color: ${brand.white};
  border-radius: 2px;
  position: relative;

  svg {
    width: 13px;
    height: 10px;
    display: none;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }
`;

const CompareText = styled.div`
  ${fonts.helvetica['73-bold-extended']};
  font-size: 14px;
  margin-left: 10px;
`;

const VariantSelector = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  width: 100%;
  gap: 10px;

  @media ${until(Device.TabletLarge)} {
    padding-left: 12px;
  }
`;

const VariantThumbnail = styled.div`
  display: flex;
  place-items: center;
  place-content: center;
  aspect-ratio: 1 / 1;
  border: 2px solid transparent;
  border-radius: 4px;
  cursor: pointer;

  &[aria-current='true'] {
    border-color: ${brand.black};
  }

  &:not([aria-current='true']):hover {
    border-color: ${brand.grey[90]};
  }

  img {
    display: block;
    border-radius: inherit;
  }
`;

const OtherVariantsCount = styled.div`
  display: flex;
  place-items: center;
  place-content: center;
  ${fonts.helvetica['83-heavy-extended']}
  font-size: 21px;
  line-height: 1em;
  color: ${brand.grey[45]};
`;

const InfoModalCta = styled.button.attrs({ type: 'button' })`
  ${ButtonReset};
  ${fonts.helvetica['63-medium-extended']};
  margin-top: 8px;
  display: flex;
  flex-direction: row;
  align-items: center;

  @media ${from(Device.Tablet)} {
    margin-top: 16px;
  }

  > svg {
    height: 14px;
    margin-top: 2px;
    margin-left: 8px;
  }
`;

const ButtonHider = styled.div`
  visibility: hidden;
`;

export const ProductCardStyles = {
  Card,
  CardContent,
  Unpublished,
  MediaWrapper,
  Media,
  Icons,
  IconWrapper,
  IconInner,
  Name,
  CodeWrapper,
  Code,
  TagWrapper,
  ProductStandardPrice,
  ProductPrice,
  Disclaimer,
  BundleDropdown,
  BottomTitle,
  BottomContent,
  Icon,
  LogoWrapper,
  CompareContainer,
  CompareBox,
  CompareText,
  VariantSelector,
  VariantThumbnail,
  OtherVariantsCount,
  InfoModalCta,
  ButtonHider,
};
